import { FeatureFlag } from "@coeff/api";
import {
  ConfigureSSO200Response,
  IdentityProvider,
  SSOAuthorizationURLResponseMode,
} from "@coeff/api-admin-ext";
import { getIdProviderDisplayName } from "@coeff/utils";
import { useFeatureGate } from "@statsig/react-bindings";
import { Divider, message } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import styled from "styled-components";

import {
  AlertExclamation,
  BorderedSelect,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  CardHeader,
  ConfirmBox,
  FlexRow,
  Form,
  Input,
  Loader,
  Modal,
  Option,
  Switch,
  Typography,
} from "../components";
import { COLORS } from "../constants";
import { AppState } from "../store";
import { isAxiosErrorWithMessage, useApiContext } from "../utils";

import { Header } from "./Dashboard/components";
import { useOAuth } from "./useOauth";

const Container = styled.div`
  padding: 20px 30px;

  .ant-btn {
    display: inline;
  }
`;

const StyledHeader = styled(Header)`
  margin-bottom: 0px;
`;

const HeaderInfo = styled.div`
  margin-bottom: 2em;
`;

const Content = styled.div`
  width: 480px;
`;

const FormGroup = styled.div`
  label {
    font-weight: 600;
    display: inline-block;
    margin-bottom: 4px;
  }
  margin-bottom: 20px;
`;

const SaveButton = styled(Button)`
  width: 200px;
  margin-top: 32px;
`;

const SetupSteps = styled.a`
  font-weight: 700;
`;

const Card = styled.div`
  background-color: ${COLORS.redBg};
  border-radius: 4px;
  padding: 6px;
  margin-bottom: 20px;

  .sectionIcon {
    display: flex;
  }
`;

const InfoCard = styled.div`
  background-color: ${COLORS.coeblue};
  border-radius: 4px;
  padding: 10px;
  margin-top: 20px;

  .title {
    font-weight: 700;
  }
`;

const CardPanel = styled.div`
  margin-left: 26px;
  margin-top: 4px;
  margin-bottom: 10px;
`;

const StyledButton = styled(Button)<{ color?: string }>`
  font-weight: 600;
  font-size: 16px;
  color: ${p => (p.color === "danger" ? COLORS.red : COLORS.coeblue5)};
`;

const DropdownWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .ant-select {
    width: 50%;
  }
`;

const OkButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
`;

const SwitcherFormGroup = styled.div`
  display: flex;
  justify-content: end;
  font-weight: 700;
  margin-bottom: 4px;

  label {
    margin-right: 12px;
  }
`;

const AdvancedSettingsWrapper = styled.div`
  margin-top: 32px;
  margin-bottom: 20px;
`;

const AdvancedSettingsHeader = styled.div`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 20px;
`;

const SettingRow = styled.div`
  margin-bottom: 8px;
`;

const SettingLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  label {
    font-weight: 600;
    margin: 0;
  }

  a {
    font-weight: 400;
    font-size: 14px;
    color: ${COLORS.coeblue5};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SettingControl = styled.div`
  margin-bottom: 8px;

  .ant-select {
    width: 100%;
  }
`;

const SettingHelpText = styled.div`
  font-size: 13px;
  color: ${COLORS.black45};
  line-height: 1.4;
`;

const HelpModalContent = styled.div`
  p {
    color: ${COLORS.black65};
    line-height: 1.6;
    margin-bottom: 16px;
  }

  ul {
    margin: 16px 0;
    padding-left: 0;
    list-style: none;
  }

  li {
    margin-bottom: 16px;
    color: ${COLORS.black65};
    line-height: 1.6;

    strong {
      color: ${COLORS.black};
      font-weight: 600;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`;

function getSSOSetupGuideLink(idProvider: IdentityProvider): string {
  if (idProvider === IdentityProvider.Okta) {
    return "https://help.coefficient.io/hc/en-us/articles/23413021347611-SSO-with-Okta";
  }
  if (idProvider === IdentityProvider.Duo) {
    return "https://help.coefficient.io/hc/en-us/articles/44192956173979-SSO-with-Duo";
  }
  if (idProvider === IdentityProvider.Entra) {
    // TODO: Replace with actual Entra SSO setup guide link when published
    return "https://help.coefficient.io/hc/en-us/articles/23413021347611-SSO-with-Okta";
  }
  return "#";
}

function getIssuerUrlLabel(idProvider: IdentityProvider): string {
  // Okta uses a tenant URL; Duo and Entra use an explicit OIDC issuer URL.
  if (idProvider === IdentityProvider.Okta) {
    return "Authentication URL";
  }
  return "Issuer URL";
}

function getIssuerUrlPlaceholder(idProvider: IdentityProvider): string {
  if (idProvider === IdentityProvider.Duo) {
    return "https://acmecorp.sso.duosecurity.com/oidc/••••••••••";
  }
  if (idProvider === IdentityProvider.Entra) {
    return "https://login.microsoftonline.com/{tenant-id}/v2.0";
  }
  return "https://acmecorp.okta.com/";
}

function getClientIdPlaceholder(idProvider: IdentityProvider): string {
  if (idProvider === IdentityProvider.Duo) {
    return "DIAAG89LX8YG92V36K91";
  }
  if (idProvider === IdentityProvider.Entra) {
    return "00000000-0000-0000-0000-000000000000";
  }
  return "fg137e559e-ff20-4eed-8d27-fb6ca1bb7b5c23";
}

export const ConfigureSSO: React.FC = () => {
  const [showSSOSavedModal, setShowSSOSavedModal] = useState<boolean>(false);
  const [showSSODisableModal, setShowSSODisableModal] = useState<boolean>(false);
  const [showSSODeleteModal, setShowSSODeleteModal] = useState<boolean>(false);

  const [clientId, setClientId] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string>("");
  const [issuerUrl, setIssuerUrl] = useState<string>("");
  const [idProvider, setIdProvider] = useState<IdentityProvider>(IdentityProvider.Okta);
  const [ssoEnabled, setSsoEnabled] = useState<boolean>(false);
  const [isNewConfiguration, setIsNewConfiguration] = useState<boolean>(false);
  const [authResponseMode, setAuthResponseMode] = useState<string>("automatic");
  const [showAuthResponseModeHelp, setShowAuthResponseModeHelp] = useState<boolean>(false);

  const { adminExtApiClient: api } = useApiContext();

  const { handleAuthorize, isAuthInProgress, handleCancel } = useOAuth();

  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const { value: enableEntraSsoAuthenticationProvider } = useFeatureGate(
    FeatureFlag.EnableEntraSsoAuthenticationProvider
  );

  const { value: enableSsoResponseModeOverride } = useFeatureGate(
    FeatureFlag.EnableSsoResponseModeOverride
  );

  const updateStates = ({
    client_id,
    client_secret,
    issuer_url,
    id_provider,
    sso_enabled,
    response_mode_override,
  }: ConfigureSSO200Response) => {
    if (client_id && client_secret && issuer_url && id_provider) {
      // SSO is already configured, set the values
      setClientId(client_id);
      setClientSecret(client_secret);
      setIssuerUrl(issuer_url);
      setIdProvider(id_provider);
      setIsNewConfiguration(false);
    } else {
      // This is a new configuration, editing would be allowed
      // set the default here, because, after delete we want to show clean form
      setClientId("");
      setClientSecret("");
      setIssuerUrl("");
      setIdProvider(IdentityProvider.Okta);
      setIsNewConfiguration(true);
    }
    setSsoEnabled(Boolean(sso_enabled));

    // Set response mode based on override or default to "automatic"
    if (response_mode_override) {
      setAuthResponseMode(response_mode_override);
    } else {
      setAuthResponseMode("automatic");
    }
  };

  useEffect(() => {
    api
      .getSSOConfig()
      .then(({ data: ssoData }) => {
        updateStates(ssoData);
      })
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    message.loading("Saving");

    try {
      // Convert authResponseMode to the proper enum value or undefined
      let responseModeOverride: SSOAuthorizationURLResponseMode | undefined;
      if (authResponseMode === "query") {
        responseModeOverride = SSOAuthorizationURLResponseMode.Query;
      } else if (authResponseMode === "form_post") {
        responseModeOverride = SSOAuthorizationURLResponseMode.FormPost;
      }
      // If "automatic", leave as undefined

      const { data: ssoConfig } = await api.configureSSO({
        configure_sso_request: {
          client_id: clientId,
          client_secret: clientSecret,
          issuer_url: issuerUrl,
          id_provider: idProvider,
          sso_enabled: ssoEnabled,
          response_mode_override: responseModeOverride,
        },
      });

      updateStates(ssoConfig);

      message.success("Saved successfully");

      setShowSSOSavedModal(true);
    } catch (error) {
      message.error(
        isAxiosErrorWithMessage(error)
          ? error.response.data.message
          : "Something went wrong. Please try again."
      );
    }
  };

  const handleDelete = async () => {
    setShowSSODeleteModal(false);

    message.loading("Deleting");

    try {
      const { data: ssoConfig } = await api.deleteSSOConfig();

      updateStates(ssoConfig);

      message.success("Deleted successfully.");
    } catch (error) {
      message.error("Something went wrong. Please try again.");
    }
  };

  const handledSSOEnabledChange = async newVal => {
    setShowSSODisableModal(false);
    setShowSSOSavedModal(false);

    message.loading("Saving");

    try {
      const { data: ssoConfig } = await api.modifySSOConfig({
        modify_sso_config_request: { sso_enabled: newVal },
      });

      updateStates(ssoConfig);

      message.success(`SSO ${newVal ? "Enabled" : "Disabled"}`);
    } catch (error) {
      message.error("Something went wrong. Please try again.");
    }
  };

  if (!clientFlags?.show_sso_config_ui) {
    return <Redirect to="/dashboard" />;
  }

  const isEditable = isNewConfiguration;

  const availableIdentityProviders = Object.values(IdentityProvider).filter(ip => {
    if (ip === IdentityProvider.Entra && !enableEntraSsoAuthenticationProvider) {
      return false;
    }
    return true;
  });

  return (
    <Container>
      <Breadcrumb>
        <BreadcrumbItem>
          <Link to={{ pathname: `/dashboard/admin` }}>Admin</Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Link to={{ pathname: `/dashboard/admin/settings` }}>Settings</Link>
        </BreadcrumbItem>
      </Breadcrumb>
      <StyledHeader>
        <h1>SSO Authentication</h1>
      </StyledHeader>
      <HeaderInfo>
        Requires users from your domain to sign in with SSO before using Coefficient.
      </HeaderInfo>

      <Content>
        <FormGroup>
          {isEditable && <label>SSO Authentication Provider</label>}
          <DropdownWrapper>
            {isEditable && (
              <BorderedSelect size="middle" value={idProvider} onChange={setIdProvider}>
                {availableIdentityProviders.map(val => (
                  <Option key={val} value={val}>
                    {getIdProviderDisplayName(val)}
                  </Option>
                ))}
              </BorderedSelect>
            )}
            <SetupSteps target="_blank" href={getSSOSetupGuideLink(idProvider)}>
              How to setup {getIdProviderDisplayName(idProvider)} SSO
            </SetupSteps>
          </DropdownWrapper>
          {!isEditable && (
            <InfoCard>
              <CardHeader
                icon={<AlertExclamation type="info" />}
                title={
                  <span>
                    {getIdProviderDisplayName(idProvider)} SSO has been configured for your domain
                  </span>
                }
              />
            </InfoCard>
          )}
        </FormGroup>

        {isEditable && <Divider />}

        {/* <Card>
          <CardHeader
            icon={<AlertExclamation />}
            title="Could not verify Client ID"
            className="card__header"
          />
          <CardPanel>
            Verify the Client ID is correct and matched the one created in {getIdProviderDisplayName(idProvider)}.
          </CardPanel>
        </Card> */}

        <Form>
          {!isEditable && (
            <SwitcherFormGroup>
              <label>Enable SSO authentication</label>
              <Switch
                checked={ssoEnabled}
                onChange={newVal =>
                  newVal ? handledSSOEnabledChange(true) : setShowSSODisableModal(true)
                }
              />
            </SwitcherFormGroup>
          )}
          <FormGroup>
            <label>{getIssuerUrlLabel(idProvider)}</label>
            <Input
              type="text"
              placeholder={getIssuerUrlPlaceholder(idProvider)}
              onChange={e => setIssuerUrl(e.currentTarget.value)}
              value={issuerUrl}
              disabled={!isEditable}
            />
          </FormGroup>
          <FormGroup>
            <label>Client ID</label>
            <Input
              type="text"
              placeholder={getClientIdPlaceholder(idProvider)}
              onChange={e => setClientId(e.currentTarget.value)}
              value={clientId}
              disabled={!isEditable}
            />
          </FormGroup>
          <FormGroup>
            <label>Client Secret</label>
            <Input
              type="password"
              placeholder="••••••••••"
              onChange={e => setClientSecret(e.currentTarget.value)}
              value={clientSecret}
              disabled={!isEditable}
            />
          </FormGroup>

          {enableSsoResponseModeOverride && (
            <AdvancedSettingsWrapper>
              <AdvancedSettingsHeader>Advanced Settings</AdvancedSettingsHeader>

              <SettingRow>
                <SettingLabelRow>
                  <label>Authorization Response Mode</label>
                  <a
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      setShowAuthResponseModeHelp(true);
                    }}
                  >
                    Help
                  </a>
                </SettingLabelRow>
                <SettingControl>
                  <BorderedSelect
                    size="middle"
                    value={authResponseMode}
                    onChange={setAuthResponseMode}
                    disabled={!isEditable}
                  >
                    <Option value="automatic">Automatic (recommended)</Option>
                    <Option value="query">Query</Option>
                    <Option value="form_post">Form Post</Option>
                  </BorderedSelect>
                </SettingControl>
                <SettingHelpText>
                  We'll choose the most secure and compatible mode based on your IdP
                </SettingHelpText>
              </SettingRow>
            </AdvancedSettingsWrapper>
          )}

          <FlexRow>
            {isEditable && (
              <SaveButton
                style={{ marginRight: "40px" }}
                loading={isAuthInProgress}
                disabled={isAuthInProgress}
                onClick={() => {
                  // Convert authResponseMode to the proper enum value or undefined for testing
                  let responseModeOverride: SSOAuthorizationURLResponseMode | undefined;
                  if (authResponseMode === "query") {
                    responseModeOverride = SSOAuthorizationURLResponseMode.Query;
                  } else if (authResponseMode === "form_post") {
                    responseModeOverride = SSOAuthorizationURLResponseMode.FormPost;
                  }

                  handleAuthorize({
                    clientId,
                    clientSecret,
                    issuerUrl,
                    idProvider,
                    responseModeOverride,
                  });
                }}
              >
                Test SSO Credentials
              </SaveButton>
            )}
            {isEditable ? (
              <SaveButton type="primary" onClick={handleSave}>
                Save
              </SaveButton>
            ) : (
              <SaveButton onClick={() => setShowSSODeleteModal(true)}>Delete</SaveButton>
            )}
          </FlexRow>
        </Form>
      </Content>

      <ConfirmBox
        title={
          <span>
            {getIdProviderDisplayName(idProvider)} SSO has been successfully configured for your
            domain.
          </span>
        }
        visible={showSSOSavedModal}
        okButton={
          <OkButtonWrapper>
            <StyledButton type="text">Yes, enable</StyledButton>
          </OkButtonWrapper>
        }
        cancelButton={<StyledButton type="text">Not now</StyledButton>}
        onOk={() => handledSSOEnabledChange(true)}
        onCancel={() => setShowSSOSavedModal(false)}
        width={420}
        closable={true}
      >
        <p>Do you want to enable SSO for your domain now?</p>
      </ConfirmBox>

      <ConfirmBox
        title="Please Confirm"
        visible={showSSODisableModal}
        okButton={
          <OkButtonWrapper>
            <StyledButton type="text">Yes, proceed</StyledButton>
          </OkButtonWrapper>
        }
        cancelButton={<StyledButton type="text">Cancel</StyledButton>}
        onOk={() => handledSSOEnabledChange(false)}
        onCancel={() => setShowSSODisableModal(false)}
        width={420}
        closable={true}
      >
        <p>Are you sure you want to disable {getIdProviderDisplayName(idProvider)} SSO?</p>
        <p>
          Users from your domain will no longer be required to sign in with{" "}
          {getIdProviderDisplayName(idProvider)} before using Coefficient.
        </p>
      </ConfirmBox>

      <ConfirmBox
        title="Please Confirm"
        visible={showSSODeleteModal}
        okButton={
          <OkButtonWrapper>
            <StyledButton type="text" color="danger">
              Delete
            </StyledButton>
          </OkButtonWrapper>
        }
        cancelButton={<StyledButton type="text">Cancel</StyledButton>}
        onOk={handleDelete}
        onCancel={() => setShowSSODeleteModal(false)}
        width={420}
        closable={true}
      >
        <p>
          Are you sure you want to delete this {getIdProviderDisplayName(idProvider)} SSO
          configuration?
        </p>
        <p>
          Users from your domain will no longer be required to sign in with{" "}
          {getIdProviderDisplayName(idProvider)} before using Coefficient.
        </p>
      </ConfirmBox>

      <Modal open={isAuthInProgress} onCancel={handleCancel} footer={null} width={420}>
        <Typography fontWeight={"bold"} fontSize="22px">
          Validating Credentials
        </Typography>

        <FlexRow style={{ marginTop: "50px", marginBottom: "40px" }}>
          <Loader />
        </FlexRow>

        <FlexRow style={{ justifyContent: "flex-end" }}>
          <Button onClick={handleCancel}>Cancel</Button>
        </FlexRow>
      </Modal>

      <Modal
        open={showAuthResponseModeHelp}
        onCancel={() => setShowAuthResponseModeHelp(false)}
        footer={null}
        width={500}
        closable={true}
      >
        <Typography fontWeight={"bold"} fontSize="20px" style={{ marginBottom: "16px" }}>
          Authorization Response Mode
        </Typography>

        <HelpModalContent>
          <p>
            This setting controls how your identity provider (IdP) sends authorization data back to
            our app during the SSO login flow. Most providers work best with the default option, but
            some require a specific mode to be fully compatible.
          </p>

          <ul>
            <li>
              <strong>Automatic (Recommended):</strong> We'll choose the most secure and compatible
              mode based on your IdP.
            </li>
            <li>
              <strong>Query:</strong> Sends the response through URL query params. Some IdPs only
              support this mode.
            </li>
            <li>
              <strong>Form Post:</strong> Sends the response using a secure form submission. This is
              generally the safest option when supported.
            </li>
          </ul>

          <p>
            If you're not sure which to pick, leave it on Automatic and you should be good to go.
          </p>

          <ModalFooter>
            <Button type="text" onClick={() => setShowAuthResponseModeHelp(false)}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => setShowAuthResponseModeHelp(false)}>
              Close
            </Button>
          </ModalFooter>
        </HelpModalContent>
      </Modal>
    </Container>
  );
};
